import { css } from '@emotion/react'
import styled from '@emotion/styled'
import React, { type CSSProperties, memo, useMemo } from 'react'

import { hoverCapableMediaQuery } from '../../code-only/hooks/use-has-hover.ts'
import { type CompoundIconName } from '../../foundations/icon/index.ts'
import { borderRadius, borderStroke, colorTokens, spacing } from '../../foundations/index.ts'
import { shadow } from '../../foundations/shadow-tokens.ts'
import { Text, type TextVariant } from '../../foundations/text/index.ts'
import { Avatar, AvatarContext } from '../avatar/index.ts'
import type { BadgeProps } from '../badge/index.ts'
import type { ButtonProps } from '../button/index.ts'
import type { ChipProps } from '../chip/index.ts'
import { Checkbox } from '../controls/checkbox/index.ts'
import { Radio, RadioGroup } from '../controls/radio/radio.tsx'
import { Spinner } from '../spinner/index.ts'
import { Tooltip } from '../tooltip/index.ts'

/** Do not use variant="dropdown" directly.
 * Only used when Row is a child of RowContainer */
export type RowVariant = 'descriptive' | 'basic' | 'standalone' | 'dropdown'

type RowState = 'enable' | 'hover' | 'focus' | 'disabled' | 'active' | 'activeFocus'

export type OrusRowSize = 'xsmall' | 'small' | 'medium' | 'large'

export type RowProps = {
  title?: React.ReactNode | undefined
  titleVariant?: TextVariant | undefined
  /** Node rendered inline next to the title (e.g. a badge). Kept beside the title text element, not inside it, so block-level content stays valid. */
  titleAdornment?: React.ReactNode | undefined
  subtitle?: React.ReactNode | undefined
  titleColor?: string | undefined
  subtitleColor?: string | undefined
  subtitleVariant?: TextVariant | undefined
  variant?: RowVariant | undefined
  size?: OrusRowSize | undefined
  checkbox?: boolean | undefined
  radio?: boolean | undefined
  selected?: boolean | undefined
  indeterminate?: boolean | undefined
  leftAvatar?: React.ReactNode | undefined
  leftIcon?: CompoundIconName | undefined
  chip?: React.ReactElement<ChipProps> | undefined
  badge?: React.ReactElement<BadgeProps> | undefined
  rightAvatar?: React.ReactNode | undefined
  rightText?: React.ReactNode | undefined
  rightTextVariant?: TextVariant | undefined
  rightTextColor?: string | undefined
  rightIcon?: CompoundIconName | undefined
  tooltip?: string | undefined
  tooltipPlacement?: 'before-badge' | 'after-badge' | undefined
  button?: React.ReactElement<ButtonProps> | undefined
  buttonGroup?: React.ReactNode | undefined
  disabled?: boolean | undefined
  isDestructive?: boolean | undefined
  isLoading?: boolean | undefined
  href?: string | undefined
  openInNewTab?: boolean | undefined
  className?: string | undefined
  onClick?: (() => void) | undefined
  ariaLabel?: string | undefined
  rightAvatarCssProps?: CSSProperties
  disableIndividualBorderRadius?: boolean
  buttonType?: 'button' | 'submit' | 'reset' | undefined
  checkboxTooltip?: string | undefined
  dataTestId?: string | undefined
}

export const Row = memo<RowProps>(function Row(props) {
  const {
    title,
    titleVariant = 'body1SemiBold',
    titleAdornment,
    subtitle,
    variant = 'basic',
    subtitleVariant = 'body2',
    size = 'xsmall',
    checkbox = false,
    selected = false,
    indeterminate = false,
    radio = false,
    leftAvatar,
    leftIcon,
    chip,
    badge,
    rightAvatar,
    rightText,
    rightTextVariant = 'body2SemiBold',
    rightIcon,
    tooltip,
    tooltipPlacement = 'after-badge',
    button,
    buttonGroup,
    disabled = false,
    isDestructive = false,
    isLoading = false,
    href,
    className,
    onClick,
    ariaLabel,
    openInNewTab = false,
    rightAvatarCssProps,
    disableIndividualBorderRadius = false,
    buttonType = 'submit',
    checkboxTooltip,
    dataTestId,
  } = props

  const padding = useMemo(() => paddingPerRowVariantAndSize[variant][size], [variant, size])
  const rowStyle = useMemo(() => rowStylePerVariantAndState[variant], [variant])
  const isChoice = checkbox || radio

  const setupLeftAvatar = useMemo(
    () =>
      leftAvatar || leftIcon ? (
        <>
          {leftAvatar || (
            <AvatarContext.Provider value={avatarDefaultContext}>
              <Avatar
                icon={leftIcon}
                color={
                  disabled
                    ? colorTokens['color-text-base-disable']
                    : isDestructive
                      ? colorTokens['color-fg-danger']
                      : colorTokens['color-text-base-main']
                }
              />
            </AvatarContext.Provider>
          )}
        </>
      ) : null,
    [leftAvatar, leftIcon, disabled, isDestructive],
  )

  const setupRightAvatar = useMemo(
    () =>
      rightAvatar || rightIcon ? (
        <>
          {rightAvatar || (
            <AvatarContext.Provider value={avatarDefaultContext}>
              <Avatar
                icon={rightIcon}
                color={
                  disabled
                    ? colorTokens['color-text-base-disable']
                    : isDestructive
                      ? colorTokens['color-fg-danger']
                      : colorTokens['color-text-base-main']
                }
              />
            </AvatarContext.Provider>
          )}
        </>
      ) : null,
    [rightAvatar, rightIcon, disabled, isDestructive],
  )

  const forwardedProps = {
    padding,
    rowStyle,
    disabled,
    isDestructive,
    roundedBorder: variant === 'standalone' && !disableIndividualBorderRadius,
    isChoice,
    onClick,
    checked: selected,
    className,
  }

  const checkboxContent = checkboxTooltip ? (
    <Tooltip title={checkboxTooltip}>
      <Checkbox
        disabled={disabled}
        size="small"
        checked={selected}
        indeterminate={indeterminate}
        onChange={() => !disabled && onClick?.()}
        onClick={(event) => event.stopPropagation()}
        aria-label={title}
        data-testid={dataTestId}
      />
    </Tooltip>
  ) : (
    <>
      <Checkbox
        disabled={disabled}
        size="small"
        checked={selected}
        indeterminate={indeterminate}
        onChange={() => !disabled && onClick?.()}
        onClick={(event) => event.stopPropagation()}
        aria-label={ariaLabel || title}
        data-testid={dataTestId}
      />
    </>
  )

  const titleColor = disabled
    ? colorTokens['color-text-base-disable']
    : isDestructive
      ? colorTokens['color-text-danger-secondary']
      : (props.titleColor ?? colorTokens['color-text-base-main'])

  const titleText = (
    <Text color={titleColor} variant={titleVariant}>
      {title}
    </Text>
  )

  const tooltipContent = tooltip ? (
    <AvatarContext.Provider value={avatarDefaultContext}>
      <Tooltip title={tooltip}>
        <Avatar
          icon={'circle-info-regular'}
          color={isDestructive ? colorTokens['color-fg-danger'] : colorTokens['color-text-base-main']}
        />
      </Tooltip>
    </AvatarContext.Provider>
  ) : null

  const content = (
    <>
      {checkbox ? (
        checkboxContent
      ) : radio ? (
        <RadioGroup>
          <Radio
            disabled={disabled}
            size="small"
            value=""
            checked={selected}
            onChange={() => !disabled && onClick?.()}
            aria-label={title?.toString()}
          />
        </RadioGroup>
      ) : null}
      {isLoading ? (
        <Spinner
          size="40"
          css={css`
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
          `}
        />
      ) : null}

      <HideOnLoadContainer isLoading={isLoading} variant={variant}>
        {setupLeftAvatar}
        {title || subtitle ? (
          <TitleAndSubtitleContainer>
            {titleAdornment != null ? (
              <TitleRow>
                {titleText}
                {titleAdornment}
              </TitleRow>
            ) : (
              titleText
            )}
            <Text
              color={
                disabled
                  ? colorTokens['color-text-base-disable']
                  : isDestructive
                    ? colorTokens['color-text-danger-secondary']
                    : (props.subtitleColor ??
                      (variant === 'dropdown'
                        ? colorTokens['color-text-base-main']
                        : colorTokens['color-text-base-basic']))
              }
              variant={subtitleVariant}
            >
              {subtitle}
            </Text>
          </TitleAndSubtitleContainer>
        ) : null}

        <div
          css={css`
            display: flex;
            align-items: center;
            margin-left: auto;
            gap: ${spacing[50]};
          `}
          style={rightAvatarCssProps}
        >
          {chip}
          {tooltipPlacement === 'before-badge' ? tooltipContent : null}
          {badge}
          {rightText != null ? (
            <Text
              color={
                disabled
                  ? colorTokens['color-text-base-disable']
                  : isDestructive
                    ? colorTokens['color-text-danger-secondary']
                    : (props.rightTextColor ?? colorTokens['color-text-base-main'])
              }
              variant={rightTextVariant}
            >
              {rightText}
            </Text>
          ) : null}
          {setupRightAvatar}
          {tooltipPlacement === 'after-badge' ? tooltipContent : null}
          {buttonGroup}
          {button}
        </div>
      </HideOnLoadContainer>
    </>
  )

  if (href) {
    return (
      <RowLink
        {...forwardedProps}
        href={disabled ? undefined : props.href}
        onClick={() => !disabled && onClick?.()}
        aria-label={ariaLabel}
        variant={variant}
        {...(openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {content}
      </RowLink>
    )
  }

  if (isChoice) {
    return (
      <RowChoice
        {...forwardedProps}
        active={selected}
        aria-label={ariaLabel}
        variant={variant}
        onClick={() => !disabled && onClick?.()}
      >
        {content}
      </RowChoice>
    )
  }

  if (onClick) {
    return (
      <RowButton
        type={buttonType}
        {...forwardedProps}
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && onClick()}
        aria-label={ariaLabel}
        variant={variant}
      >
        {content}
      </RowButton>
    )
  }

  return (
    <RowBase {...forwardedProps} variant={variant} disabled={disabled}>
      {content}
    </RowBase>
  )
})

const avatarDefaultContext = { size: '30' } as const

const HideOnLoadContainer = styled.div<{ isLoading: boolean; variant: RowVariant }>`
  visibility: ${({ isLoading }) => (isLoading ? 'hidden' : 'visible')};
  display: flex;
  flex: 1;
  align-items: center;
  gap: ${({ variant }) => (variant === 'dropdown' ? spacing[30] : spacing[50])};
  width: 100%;
`

type RowStylingProps = {
  padding: string
  rowStyle: { [key in RowState]?: RowStyle }
  disabled: boolean
  roundedBorder: boolean
  variant: RowVariant
  isDestructive: boolean
}

const rowStyleMixin = ({ padding, rowStyle, disabled, roundedBorder, isDestructive }: RowStylingProps) => css`
  position: relative;
  display: flex;
  align-items: center;

  gap: ${spacing[50]};
  padding: ${padding};
  border-radius: ${roundedBorder ? borderRadius['20'] : 0};

  ${hoverCapableMediaQuery} {
    &:hover {
      background-color: ${
        disabled ? '' : isDestructive ? colorTokens['color-bg-danger-secondary-hover'] : rowStyle.hover?.backgroundColor
      };
      outline: ${disabled ? '' : rowStyle.hover?.outline};
      outline-offset: ${disabled ? '' : rowStyle.hover?.outlineOffset};
      border-radius: ${disabled ? '' : rowStyle.hover?.borderRadius};
    }
  }

  &:focus-visible {
    background-color: ${
      disabled ? '' : isDestructive ? colorTokens['color-bg-danger-secondary-hover'] : rowStyle.focus?.backgroundColor
    };
    outline: ${disabled ? '' : rowStyle.focus?.outline};
    outline-offset: ${disabled ? '' : rowStyle.focus?.outlineOffset};
  }

  background-color: ${disabled ? rowStyle.disabled?.backgroundColor : rowStyle.enable?.backgroundColor};
  outline: ${disabled ? rowStyle.disabled?.outline : rowStyle.enable?.outline};
  outline-offset: ${disabled ? rowStyle.disabled?.outlineOffset : rowStyle.enable?.outlineOffset};
`

const RowBase = styled.div<RowStylingProps>`
  ${(props) => rowStyleMixin(props)}
`

const RowButton = styled.button<RowStylingProps>`
  ${(props) => rowStyleMixin(props)}
  cursor: ${({ disabled, variant }) => (disabled || variant === 'descriptive' ? 'default' : 'pointer')};

  max-width: 100%;
  text-align: inherit;
  border: none;
  color: inherit;
  font: inherit;
`

const RowLink = styled.a<RowStylingProps>`
  ${(props) => rowStyleMixin(props)}
  color: inherit;
  text-decoration: inherit;
  cursor: ${({ disabled, variant }) => (disabled || variant === 'descriptive' ? 'default' : 'pointer')};
`

const RowChoice = styled.div<RowStylingProps & { active: boolean }>`
  ${(props) => rowStyleMixin(props)}
  cursor: ${({ disabled, variant }) => (disabled || variant === 'descriptive' ? 'default' : 'pointer')};

  ${({ rowStyle, disabled }) =>
    disabled
      ? ''
      : css`
          ${hoverCapableMediaQuery} {
            &:hover {
              box-shadow: ${rowStyle.hover?.boxShadow};
            }
          }
        `}

  ${({ rowStyle, active }) =>
    active
      ? css`
          outline: ${rowStyle.active?.outline} !important;
          outline-offset: ${rowStyle.active?.outlineOffset} !important;
          border-color: transparent;

          &:hover {
            border-color: transparent;
          }

          &:focus-within {
            outline: ${rowStyle.activeFocus?.outline};
            outline-offset: ${rowStyle.activeFocus?.outlineOffset};
            box-shadow: ${rowStyle.activeFocus?.boxShadow};
            border-color: transparent;
          }
        `
      : ''}
`
const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[50]};
`
const TitleAndSubtitleContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow: hidden;
  min-width: 120px;

  & > * {
    white-space: pre-wrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`
const paddingPerRowVariantAndSize: Record<RowVariant, Record<OrusRowSize, string>> = {
  descriptive: {
    xsmall: `${spacing[20]} 0`,
    small: `${spacing[30]} 0`,
    medium: `${spacing[40]} 0`,
    large: `${spacing[50]} 0`,
  },
  basic: {
    xsmall: `${spacing[20]} 0`,
    small: `${spacing[30]} 0`,
    medium: `${spacing[40]} 0`,
    large: `${spacing[50]} 0`,
  },
  standalone: {
    xsmall: `${spacing[20]} ${spacing[50]}`,
    small: `${spacing[30]} ${spacing[50]}`,
    medium: `${spacing[40]} ${spacing[50]}`,
    large: `${spacing[50]} ${spacing[50]}`,
  },
  dropdown: {
    xsmall: `${spacing[20]} ${spacing[30]}`,
    small: `${spacing[30]} ${spacing[30]}`,
    medium: `${spacing[40]} ${spacing[30]}`,
    large: `${spacing[50]} ${spacing[30]}`,
  },
}

type RowStyle = {
  backgroundColor?: string
  outline?: string
  outlineOffset?: string
  boxShadow?: string
  borderRadius?: string
}
const rowStylePerVariantAndState: Record<RowVariant, { [key in RowState]?: RowStyle }> = {
  descriptive: {
    enable: { backgroundColor: colorTokens['color-bg-base-normal'] },
    hover: { backgroundColor: colorTokens['color-bg-base-normal'] },
    focus: { backgroundColor: colorTokens['color-bg-base-normal'] },
    disabled: { backgroundColor: colorTokens['color-bg-base-normal'] },
  },
  basic: {
    enable: { backgroundColor: colorTokens['color-bg-base-normal'] },
    hover: { backgroundColor: colorTokens['color-bg-base-hover'] },
    focus: { backgroundColor: colorTokens['color-bg-base-focus'] },
    disabled: { backgroundColor: colorTokens['color-bg-base-normal'] },
  },
  dropdown: {
    enable: { backgroundColor: colorTokens['color-bg-base-normal'] },
    hover: {
      backgroundColor: colorTokens['color-bg-base-hover'],
      boxShadow: shadow.bottom['05'],
      borderRadius: borderRadius['20'],
    },
    focus: { backgroundColor: colorTokens['color-bg-base-focus'] },
    disabled: { backgroundColor: colorTokens['color-bg-base-normal'] },
    active: { backgroundColor: colorTokens['color-bg-base-normal'] },
    activeFocus: { backgroundColor: colorTokens['color-bg-base-focus'], boxShadow: shadow.bottom['05'] },
  },
  standalone: {
    enable: {
      backgroundColor: colorTokens['color-bg-base-normal'],
      outline: `${borderStroke[20]} solid ${colorTokens['color-stroke-base-main']}`,
      outlineOffset: `-${borderStroke[20]}`,
    },
    hover: {
      backgroundColor: colorTokens['color-bg-base-hover'],
      outline: `${borderStroke[20]} solid ${colorTokens['color-stroke-base-main-hover']}`,
      outlineOffset: `-${borderStroke[20]}`,
      boxShadow: shadow.bottom['05'],
    },
    focus: {
      backgroundColor: colorTokens['color-bg-base-focus'],
      outline: `${borderStroke[20]} solid ${colorTokens['color-stroke-base-main-active']}`,
      outlineOffset: `-${borderStroke[20]}`,
    },
    disabled: {
      backgroundColor: colorTokens['color-bg-base-normal'],
      outline: `${borderStroke[20]} solid ${colorTokens['color-stroke-base-main-disable']}`,
      outlineOffset: `-${borderStroke[20]}`,
    },
    active: {
      backgroundColor: colorTokens['color-bg-base-normal'],
      outline: `${borderStroke[30]} solid ${colorTokens['color-stroke-base-main-selected']}`,
      outlineOffset: `-${borderStroke[30]}`,
    },
    activeFocus: {
      backgroundColor: colorTokens['color-bg-base-focus'],
      outline: `${borderStroke[30]} solid ${colorTokens['color-stroke-base-main-selected']}`,
      outlineOffset: `-${borderStroke[30]}`,
      boxShadow: shadow.bottom['05'],
    },
  },
}
